import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Info, Users } from 'lucide-react';
import {
  AppButton, AppModal, Callout, Checkbox, EmptyState, SearchInput, UserAvatar, cn,
} from '../ui';
import Shimmer from '../Common/Shimmer';
import { importInstructorsToTeam, listTeamInstructorCandidates } from '../../utils/cmsAdminApi';

// ─── ImportInstructorsModal — copy an instructor's details into a profile ─
//
// A COPY, once, not a link. After this the website profile is edited on the
// Team tab and the LMS account is edited under Instructors, and neither
// follows the other — which is what the institute asked for: the public copy
// is marketing and the account is access, and they should be allowed to say
// different things.
//
// Instructors already imported stay in the list, ticked and locked, because
// "where did Priya go" is a worse question than seeing her marked as added.
//
// Props:
//   show       — boolean
//   onClose    — ()
//   onImported — (message)
//   notify     — (message, type)

const ImportInstructorsModal = ({ show, onClose, onImported, notify }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTeamInstructorCandidates();
      setRows(res.data || []);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    setPicked(new Set());
    setSearch('');
    load();
  }, [show, load]);

  const available = rows.filter((r) => !r.imported);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.name, r.designation]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(q)));
  }, [rows, search]);

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const shownAvailable = shown.filter((r) => !r.imported);
  const allShownPicked = shownAvailable.length > 0 && shownAvailable.every((r) => picked.has(r._id));
  const toggleAllShown = () => setPicked((prev) => {
    const next = new Set(prev);
    if (allShownPicked) shownAvailable.forEach((r) => next.delete(r._id));
    else shownAvailable.forEach((r) => next.add(r._id));
    return next;
  });

  const submit = async () => {
    const ids = [...picked];
    if (!ids.length) return;
    setSaving(true);
    try {
      const res = await importInstructorsToTeam(ids);
      const created = res?.data?.created ?? ids.length;
      onImported(created === 1
        ? '1 profile was added to your website team'
        : `${created} profiles were added to your website team`);
    } catch (err) {
      notify(err?.response?.data?.message || 'Could not import', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      show={show}
      onHide={onClose}
      title="Import from instructors"
      size="lg"
      footer={(
        <>
          <AppButton variant="outline" onClick={onClose} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={submit} disabled={saving || picked.size === 0}>
            {saving
              ? 'Importing…'
              : picked.size === 0
                ? 'Import'
                : `Import ${picked.size} ${picked.size === 1 ? 'profile' : 'profiles'}`}
          </AppButton>
        </>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Callout tone="info" icon={<Info size={14} aria-hidden="true" />}>
          Their name, role, experience, bio, photo and LinkedIn are <strong>copied</strong> into a
          website profile you can then edit freely. Changing the instructor afterwards does not
          change the website, and nothing about their LMS access changes here.
        </Callout>

        {loading && <Shimmer height={56} count={4} />}

        {!loading && failed && (
          <Callout tone="danger" role="alert">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <span>Your instructors could not be loaded.</span>
              <AppButton size="sm" variant="outline" onClick={load}>Try again</AppButton>
            </div>
          </Callout>
        )}

        {!loading && !failed && rows.length === 0 && (
          <EmptyState
            icon={<Users size={36} />}
            title="No instructors to import"
            description="You have no active instructors yet. Add a website profile by hand instead — it needs no LMS account."
          />
        )}

        {!loading && !failed && rows.length > 0 && (
          <>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search instructors…"
                className="tw-w-full sm:tw-w-64"
              />
              {shownAvailable.length > 1 && (
                <AppButton size="sm" variant="ghost" onClick={toggleAllShown}>
                  {allShownPicked ? 'Clear selection' : `Select all ${shownAvailable.length}`}
                </AppButton>
              )}
            </div>

            {available.length === 0 && (
              <p className="tw-m-0 tw-text-sm tw-text-content-subtle">
                Every instructor has already been imported. Edit their website copy on the Team tab.
              </p>
            )}

            <ul className="tw-m-0 tw-flex tw-max-h-80 tw-list-none tw-flex-col tw-gap-1.5 tw-overflow-y-auto tw-p-0">
              {shown.map((row) => (
                <li
                  key={row._id}
                  className={cn(
                    'tw-flex tw-items-center tw-gap-3 tw-rounded-sm tw-border tw-border-solid',
                    'tw-border-line tw-px-3 tw-py-2',
                    row.imported ? 'tw-bg-surface-alt' : 'tw-bg-surface',
                  )}
                >
                  {row.imported ? (
                    <span
                      className="tw-inline-flex tw-h-4 tw-w-4 tw-shrink-0 tw-items-center tw-justify-center tw-text-success"
                      aria-hidden="true"
                    >
                      <Check size={16} />
                    </span>
                  ) : (
                    <Checkbox
                      checked={picked.has(row._id)}
                      onChange={() => toggle(row._id)}
                      aria-label={`Import ${row.name}`}
                    />
                  )}
                  <UserAvatar name={row.name} src={row.photoUrl || undefined} size="sm" />
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                    <span className="tw-truncate tw-text-sm tw-text-content">{row.name}</span>
                    {(row.designation || row.experience) && (
                      <span className="tw-truncate tw-text-xs tw-text-content-subtle">
                        {[row.designation, row.experience].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>
                  {row.imported && (
                    <span className="tw-shrink-0 tw-text-xs tw-text-content-subtle">Already added</span>
                  )}
                </li>
              ))}
              {shown.length === 0 && (
                <li className="tw-rounded-sm tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-5 tw-text-center tw-text-sm tw-text-content-subtle">
                  No instructor matches “{search.trim()}”.
                </li>
              )}
            </ul>
          </>
        )}
      </div>
    </AppModal>
  );
};

export default ImportInstructorsModal;
